import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery, type UseQueryOptions } from '@tanstack/vue-query'
import { useAuth } from '@/stores/auth'
import { getPlayerStats } from '@/api/getPlayerStats'
import { getUserRankData } from '@/lib/utils'

type PlayerStatsProps = {
  filter?: string
  gameType?: string
  options?: Partial<Omit<UseQueryOptions, 'queryKey' | 'queryFn'>>
  customHash?: string
}

export function usePlayerStats({
  filter = 'tags:eq:cm',
  gameType,
  options,
  customHash
}: PlayerStatsProps = {}) {
  const { player } = storeToRefs(useAuth())

  const playerId = computed(() => player.value?.player_id)

  const {
    data: playerStats,
    isPending,
    isError
  } = useQuery({
    queryKey: [customHash ?? 'player-stats', playerId, gameType, filter],
    queryFn: async () => {
      if (!playerId.value) {
        throw new Error('Player ID is required')
      }
      return getPlayerStats(playerId.value, gameType, filter)
    },
    enabled: !!playerId.value,
    staleTime: 1000 * 60 * 5,
    retry: 1,
    refetchOnWindowFocus: false,
    ...options
  })

  const errorMessage = computed(() => {
    if (isError.value) {
      return 'Failed to fetch player stats'
    }
    return null
  })

  const rankData = computed(() => getUserRankData(playerStats.value?.rank?.score || 0))

  return {
    playerStats,
    errorMessage,
    rankData,
    isPending,
    isError
  }
}
